/**
 * @file strings.ts
 * @description All user-facing strings for Fruit Slasher, collected in one typed
 * constant so a second locale (e.g. `id` for Indonesian) can be added without
 * touching any UI component. Template strings are typed functions — call them at
 * the point of rendering to get the final copy.
 *
 * Language: English (playful arcade register)
 * Rationale: Game targets a global casual audience aged 16-35 (PRD §1). English
 * maximises reach and shareability out of the box; the structure is i18n-ready
 * so a Bahasa Indonesia locale can be bolted on without touching UI components.
 *
 * Usage:
 *   import { STRINGS } from '@/content/strings';
 *   label.text = STRINGS.hud.score;
 *   popup.text = STRINGS.combo.slashPopup(3);
 */

// ---------------------------------------------------------------------------
// Locale type contract — each locale must satisfy this shape exactly.
// ---------------------------------------------------------------------------

/** Template function that produces a string from a numeric count. */
export type CountFn = (count: number) => string;

/** Template function that produces a string from a numeric score. */
export type ScoreFn = (score: number) => string;

/** Template function that produces a string from a player name. */
export type NameFn = (name: string) => string;

export interface FruitSlasherStrings {
  /** Onboarding / calibration flow shown before the first game. */
  onboarding: {
    /** Main headline on the start screen. */
    headline: string;
    /** One-liner tagline under the headline. */
    tagline: string;
    /** Step 1 instruction: how to position the hand. */
    positionHand: string;
    /** Step 2 instruction: lighting requirement. */
    lightingTip: string;
    /** Explanation of what the on-screen cursor is. */
    cursorExplainer: string;
    /** Explanation of how dwell-select works. */
    dwellExplainer: string;
    /** Label on the primary call-to-action button. */
    startButton: string;
    /** Subtitle shown while the hand-tracking model loads. */
    loadingModel: string;
  };

  /** Camera permission flow strings. */
  camera: {
    /** Pre-permission-prompt explainer banner. */
    permissionExplainer: string;
    /** Privacy notice shown alongside the explainer. */
    privacyNotice: string;
    /** Short label for the "allow camera" call-to-action. */
    allowButton: string;
    /** Full message shown when the user denies camera access. */
    deniedMessage: string;
    /** Short note reassuring keyboard/tap users after a denial. */
    deniedFallbackNote: string;
  };

  /** READY gate — shown between mode select (or retry) and gameplay. */
  ready: {
    /** Overlay headline. */
    headline: string;
    /** Shown while the camera stream + tracking model initialise. */
    startingCamera: string;
    /** Prompt asking the player to put their hand in frame. */
    showHand: string;
    /** Confirmation flash once a hand is being tracked. */
    handDetected: string;
    /** Note shown when entering the countdown without hand tracking. */
    touchModeNote: string;
    /** Button label to skip the hand wait and play with touch/mouse. */
    skipButton: string;
    /** Button label to back out of the gate to the main menu. */
    backButton: string;
  };

  /** Runtime tracking-state hints rendered in-game or as overlays. */
  hints: {
    /** Shown when no hand is detected for ~1 second. */
    noHandDetected: string;
    /** Shown when the tracking confidence drops (low-light signal). */
    lowLightWarning: string;
    /** Shown when tracking was active then abruptly lost mid-game. */
    trackingLost: string;
    /** Brief tip shown once during the first session. */
    firstTimeTip: string;
  };

  /** Mode-select screen. */
  modeSelect: {
    /** Screen headline. */
    headline: string;
    /** Name of Classic mode. */
    classicName: string;
    /** Short description of Classic mode shown on the card. */
    classicDescription: string;
    /** Bullet points for Classic mode (displayed as a list). */
    classicDetails: readonly string[];
    /** Name of Zen mode. */
    zenName: string;
    /** Short description of Zen mode shown on the card. */
    zenDescription: string;
    /** Bullet points for Zen mode (displayed as a list). */
    zenDetails: readonly string[];
  };

  /** HUD labels — keep these short (fits tight arcade UI). */
  hud: {
    /** Score counter label. */
    score: string;
    /** Lives counter label (Classic). */
    lives: string;
    /** Combo counter label. */
    combo: string;
    /** Countdown timer label (Zen). */
    timer: string;
    /** Mute toggle label (accessible name). */
    muteButton: string;
    /** Unmute toggle label (accessible name). */
    unmuteButton: string;
    /** Pause button accessible name. */
    pauseButton: string;
    /** Label for the active power-up countdown bar (generic fallback). */
    powerUpActive: string;
    /** Label for double-points power-up countdown bar. */
    doublePointsActive: string;
    /** Label for freeze power-up countdown bar. */
    freezeActive: string;
    /** Label for magnet power-up countdown bar. */
    magnetActive: string;
  };

  /** Combo / score popup strings shown in-world at the slash point. */
  combo: {
    /**
     * Main combo popup label.
     * @example slashPopup(2) => "Double Slash!  x2"
     * @example slashPopup(5) => "Mega Slash!  x5"
     */
    slashPopup: CountFn;
    /** Shown on a critical-fruit hit (golden fruit). */
    criticalHit: string;
    /** Shown at frenzy-wave start. */
    frenzyStart: string;
    /** Rotten-fruit penalty popup (negative score). */
    rottenPenalty: ScoreFn;
  };

  /** Pause screen. */
  pause: {
    /** Overlay headline. */
    headline: string;
    /** Resume button label. */
    resumeButton: string;
    /** Quit-to-menu button label. */
    quitButton: string;
    /** Confirmation prompt before quitting. */
    quitConfirm: string;
    /** "Yes, quit" confirmation button. */
    quitConfirmYes: string;
    /** "No, keep playing" cancellation button. */
    quitConfirmNo: string;
  };

  /** Game-over screen. */
  gameOver: {
    /** Standard game-over headline. */
    headline: string;
    /** Variant headline shown when a new high score is set. */
    newHighScoreHeadline: string;
    /** Score recap prefix. */
    scoreLabel: string;
    /** Mode label prefix shown in the recap. */
    modeLabel: string;
    /** Retry button label. */
    retryButton: string;
    /** Back-to-menu button label. */
    menuButton: string;
    /** Share button label. */
    shareButton: string;
    /** Text displayed while the share card is being generated. */
    sharePreparing: string;
    /** Fallback text if Web Share API is unavailable (desktop download). */
    shareDownloadFallback: string;
  };

  /** Share card copy (embedded in the generated PNG canvas). */
  shareCard: {
    /** Game title as it appears on the share card. */
    gameTitle: string;
    /** Tagline on the card. */
    tagline: string;
    /** Score display prefix on the card. */
    scorePrefix: string;
    /** Mode label on the card. */
    modePrefix: string;
    /** Flavour call-to-action at the bottom of the card. */
    callToAction: string;
  };

  /** Leaderboard screen. */
  leaderboard: {
    /** Screen / section headline. */
    headline: string;
    /** Classic-mode tab label. */
    classicTab: string;
    /** Zen-mode tab label. */
    zenTab: string;
    /** Column header: rank position. */
    rankHeader: string;
    /** Column header: player name. */
    nameHeader: string;
    /** Column header: score. */
    scoreHeader: string;
    /** Column header: date. */
    dateHeader: string;
    /** Empty-state message when no scores exist yet. */
    emptyState: string;
    /** Prompt asking the player to enter their name for the leaderboard. */
    nameEntryPrompt: string;
    /** Placeholder text inside the name input field. */
    nameEntryPlaceholder: string;
    /** Character-limit hint shown below the name input. */
    nameEntryLimit: string;
    /** Submit-name button label. */
    nameEntrySubmit: string;
    /** Default name used when the player submits a blank entry. */
    defaultPlayerName: string;
    /** "Top 10" section header. */
    topTenHeader: string;
    /** Row highlight suffix for the player's own entry. */
    youSuffix: string;
  };

  /** Display names for power-ups (shown in HUD and spawn animations). */
  powerUps: {
    freeze: string;
    doublePoints: string;
    magnet: string;
  };

  /** Display names for obstacles (used in tooltips and tutorial copy). */
  obstacles: {
    bomb: string;
    ice: string;
    rottenFruit: string;
  };

  /** Loading / initialisation states. */
  loading: {
    /** Generic loading label used before a more specific message is available. */
    generic: string;
    /** Shown while the MediaPipe hand-tracking model is downloading. */
    modelDownload: string;
    /** Shown while the model is initialising after download. */
    modelInit: string;
    /** Shown when loading completes. */
    ready: string;
  };
}

// ---------------------------------------------------------------------------
// English strings — the authoritative locale.
// ---------------------------------------------------------------------------

export const STRINGS: FruitSlasherStrings = {
  // -------------------------------------------------------------------------
  /** @section Onboarding & Calibration */
  // -------------------------------------------------------------------------
  onboarding: {
    headline: 'Fruit Slasher',
    tagline: 'Slice fruit with your bare hands.',

    positionHand:
      'Hold your hand about 30–60 cm from the camera, palm facing the screen. ' +
      'Keep your whole hand in the frame — the game tracks your index fingertip.',

    lightingTip:
      'Make sure your face and hands are well-lit. Avoid bright light directly behind you, ' +
      'and turn on a lamp if your room is dim — tracking works best in good light.',

    cursorExplainer:
      'The glowing circle on screen follows your index fingertip. ' +
      'That is your cursor — use it to aim and navigate.',

    dwellExplainer:
      'To select a button, hold your cursor over it for about one second. ' +
      'Watch the ring fill up — when it is full, you have selected it. ' +
      'You can also tap or click any button the normal way.',

    startButton: 'Start',

    loadingModel:
      'Loading hand-tracking model… This takes a few seconds on the first visit.',
  },

  // -------------------------------------------------------------------------
  /** @section Camera Permission */
  // -------------------------------------------------------------------------
  camera: {
    permissionExplainer:
      'Fruit Slasher needs your camera to track your hand movements ' +
      'so you can slash fruit in real time.',

    privacyNotice:
      'Your camera feed is processed entirely on your device. ' +
      'No video or image data is ever sent to any server.',

    allowButton: 'Allow Camera',

    deniedMessage:
      'Camera access was blocked. You can still navigate menus and play ' +
      'using tap or click — hand-gesture controls will not be available until ' +
      'you allow camera access in your browser settings.',

    deniedFallbackNote:
      'Tip: tap or click any button to navigate without hand gestures.',
  },

  // -------------------------------------------------------------------------
  /** @section READY Gate (pre-game camera/hand confirmation + countdown) */
  // -------------------------------------------------------------------------
  ready: {
    headline: 'Get Ready',
    startingCamera: 'Starting camera…',
    showHand: 'Show your hand to the camera',
    handDetected: 'Hand detected — here we go!',
    touchModeNote: 'Playing with touch / mouse',
    skipButton: 'Skip — play with touch',
    backButton: 'Back to Menu',
  },

  // -------------------------------------------------------------------------
  /** @section Runtime Hints */
  // -------------------------------------------------------------------------
  hints: {
    noHandDetected:
      'No hand detected — show your hand to the camera to start slashing.',

    lowLightWarning:
      'Low light detected. Try moving to a brighter spot for better tracking.',

    trackingLost:
      'Tracking lost. Move your hand back into frame to continue.',

    firstTimeTip:
      'Swipe your hand quickly to slash fruit. Slow and gentle moves will not register as slashes.',
  },

  // -------------------------------------------------------------------------
  /** @section Mode Select */
  // -------------------------------------------------------------------------
  modeSelect: {
    headline: 'Choose Your Mode',

    classicName: 'Classic',
    classicDescription: 'Survive as long as you can.',
    classicDetails: [
      '3 lives — do not miss fruit',
      'Dodge the bombs or it is game over',
      'Gets faster the longer you last',
      'High scores saved locally',
    ],

    zenName: 'Zen',
    zenDescription: 'Chase the highest score in 60 seconds.',
    zenDetails: [
      '60-second time limit',
      'No bombs, no lives lost',
      'Combo multipliers still apply',
      'Perfect for quick sessions',
    ],
  },

  // -------------------------------------------------------------------------
  /** @section HUD Labels */
  // -------------------------------------------------------------------------
  hud: {
    score: 'Score',
    lives: 'Lives',
    combo: 'Combo',
    timer: 'Time',
    muteButton: 'Mute sound',
    unmuteButton: 'Unmute sound',
    pauseButton: 'Pause game',
    powerUpActive: 'Power-up active',
    doublePointsActive: 'Double Points',
    freezeActive: 'Freeze',
    magnetActive: 'Magnet',
  },

  // -------------------------------------------------------------------------
  /** @section Combo & Score Popups */
  // -------------------------------------------------------------------------
  combo: {
    /**
     * Returns the combo popup string for a given slash count.
     * Counts 2-4 use named prefixes; 5+ become "Mega".
     */
    slashPopup: (count: number): string => {
      const prefix =
        count === 2 ? 'Double' :
        count === 3 ? 'Triple' :
        count === 4 ? 'Quad'   :
                      'Mega';
      return `${prefix} Slash!  ×${count}`;
    },

    criticalHit: 'CRITICAL!  ×5',

    frenzyStart: 'FRENZY!',

    /**
     * Returns the penalty popup string for a rotten-fruit slash.
     * @example rottenPenalty(50) => "−50 Rotten!"
     */
    rottenPenalty: (points: number): string => `−${points} Rotten!`,
  },

  // -------------------------------------------------------------------------
  /** @section Pause Screen */
  // -------------------------------------------------------------------------
  pause: {
    headline: 'Paused',
    resumeButton: 'Resume',
    quitButton: 'Quit to Menu',
    quitConfirm: 'Quit this game? Your progress will be lost.',
    quitConfirmYes: 'Quit',
    quitConfirmNo: 'Keep Playing',
  },

  // -------------------------------------------------------------------------
  /** @section Game Over */
  // -------------------------------------------------------------------------
  gameOver: {
    headline: 'Game Over',
    newHighScoreHeadline: 'New High Score!',
    scoreLabel: 'Your Score',
    modeLabel: 'Mode',
    retryButton: 'Play Again',
    menuButton: 'Main Menu',
    shareButton: 'Share Score',
    sharePreparing: 'Generating share card…',
    shareDownloadFallback: 'Score card saved to your Downloads.',
  },

  // -------------------------------------------------------------------------
  /** @section Share Card (text rendered onto the generated PNG canvas) */
  // -------------------------------------------------------------------------
  shareCard: {
    gameTitle: 'Fruit Slasher',
    tagline: 'Slice fruit with your bare hands.',
    scorePrefix: 'Score:',
    modePrefix: 'Mode:',
    callToAction: 'Play free at fruitsslasher.com',
  },

  // -------------------------------------------------------------------------
  /** @section Leaderboard */
  // -------------------------------------------------------------------------
  leaderboard: {
    headline: 'Leaderboard',
    classicTab: 'Classic',
    zenTab: 'Zen',
    rankHeader: '#',
    nameHeader: 'Player',
    scoreHeader: 'Score',
    dateHeader: 'Date',
    emptyState:
      'No scores yet. Be the first to make it onto the board!',
    nameEntryPrompt:
      'You made the top 10! Enter your name for the leaderboard.',
    nameEntryPlaceholder: 'Your name',
    nameEntryLimit: 'Up to 16 characters',
    nameEntrySubmit: 'Submit',
    defaultPlayerName: 'Player',
    topTenHeader: 'Top 10',
    youSuffix: '(you)',
  },

  // -------------------------------------------------------------------------
  /** @section Power-Up Display Names */
  // -------------------------------------------------------------------------
  powerUps: {
    freeze: 'Freeze',
    doublePoints: 'Double Points',
    magnet: 'Magnet',
  },

  // -------------------------------------------------------------------------
  /** @section Obstacle Display Names */
  // -------------------------------------------------------------------------
  obstacles: {
    bomb: 'Bomb',
    ice: 'Ice',
    rottenFruit: 'Rotten Fruit',
  },

  // -------------------------------------------------------------------------
  /** @section Loading / Initialisation States */
  // -------------------------------------------------------------------------
  loading: {
    generic: 'Loading…',
    modelDownload:
      'Downloading hand-tracking model…',
    modelInit:
      'Starting hand tracker…',
    ready: 'Ready!',
  },
} as const;

// FruitSlasherStrings is already exported at its declaration above.
// CountFn, ScoreFn, and NameFn are also exported at their declarations.
